import { useCallback, useEffect, useState } from "react";
import type { Dataset } from "./lib/types";
import { loadDataset } from "./lib/data";
import { applyTheme, getInitialTheme, type Theme } from "./lib/theme";
import { useLiveClock } from "./lib/liveClock";
import { useAuth } from "./lib/auth";
import Topbar from "./components/Topbar";
import Sidebar from "./components/Sidebar";
import Footer from "./components/Footer";
import ToastHost, { showToast } from "./components/Toast";
import LoginScreen from "./components/LoginScreen";
import Executive from "./tabs/Executive";
import PortfolioBilling from "./tabs/PortfolioBilling";
import ARCustomerRisk from "./tabs/ARCustomerRisk";
import Customer360 from "./tabs/Customer360";
import NetIncomeForecast from "./tabs/NetIncomeForecast";
import Simulator from "./tabs/Simulator";
import DecisionPack from "./tabs/DecisionPack";
import Cortex from "./tabs/Cortex";
import Industry from "./tabs/Industry";
import KpiReference from "./tabs/KpiReference";
import Architecture from "./tabs/Architecture";
import Faq from "./tabs/Faq";
import About from "./tabs/About";
import Chat from "./tabs/Chat";

export type TabId =
  | "executive"
  | "portfolio-billing"
  | "ar-customer-risk"
  | "customer-360"
  | "net-income-forecast"
  | "simulator"
  | "decision-pack"
  | "industry"
  | "cortex"
  | "chat"
  | "kpi-reference"
  | "architecture"
  | "faq"
  | "about";

export interface NavigateOpts {
  customerAccount?: string;
}

export type Navigate = (tab: TabId, opts?: NavigateOpts) => void;

export default function App() {
  const [theme, setTheme] = useState<Theme>(getInitialTheme());
  const [tab, setTab] = useState<TabId>("executive");
  const [data, setData] = useState<Dataset | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<string | null>(null);
  const live = useLiveClock(30_000);
  const auth = useAuth();

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  useEffect(() => {
    if (!auth.user) return;
    loadDataset()
      .then((d) => {
        setData(d);
        if (d.customers[0]) setSelectedCustomer(d.customers[0].customer_account);
      })
      .catch((e) => setErr(String(e?.message ?? e)));
  }, [auth.user]);

  const navigate = useCallback<Navigate>((target, opts) => {
    if (opts?.customerAccount) setSelectedCustomer(opts.customerAccount);
    setTab(target);
    queueMicrotask(() => window.scrollTo?.({ top: 0, behavior: "smooth" }));
  }, []);

  // Auth gate — show login screen until authenticated.
  if (auth.loading) {
    return (
      <div className="app">
        <div className="app-loading">
          <div className="spinner" />
        </div>
      </div>
    );
  }
  if (!auth.user) {
    return (
      <>
        <LoginScreen onSignIn={auth.signIn} />
        <ToastHost />
      </>
    );
  }

  if (err) {
    return (
      <div className="app">
        <div className="app-loading">
          <h2 style={{ color: "var(--critical)", fontSize: 16, fontWeight: 600 }}>Failed to load data</h2>
          <p style={{ fontFamily: "var(--mono)", fontSize: 12 }}>{err}</p>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="app">
        <div className="app-loading">
          <div className="spinner" />
          <p>Loading Cashflow Sentinel…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="app has-sidebar">
      <Sidebar active={tab} onTabChange={(t) => navigate(t)} />
      <div className="app-shell">
        <Topbar
          active={tab}
          theme={theme}
          onThemeToggle={() => setTheme((t) => (t === "dark" ? "light" : "dark"))}
          asOf={data.meta.asOfDisplay}
          live={live}
          user={auth.user}
          onSignOut={() => {
            auth.signOut();
            showToast("Signed out", "info");
          }}
        />
        <main className="main">
        {tab === "executive" && <Executive data={data} navigate={navigate} tick={live.tick} />}
        {tab === "portfolio-billing" && <PortfolioBilling data={data} tick={live.tick} />}
        {tab === "ar-customer-risk" && <ARCustomerRisk data={data} navigate={navigate} tick={live.tick} />}
        {tab === "customer-360" && (
          <Customer360
            data={data}
            selectedAccount={selectedCustomer ?? data.customers[0]?.customer_account ?? ""}
            onSelectCustomer={setSelectedCustomer}
            tick={live.tick}
          />
        )}
        {tab === "net-income-forecast" && <NetIncomeForecast data={data} />}
        {tab === "simulator" && <Simulator data={data} />}
        {tab === "decision-pack" && <DecisionPack data={data} />}
        {tab === "industry" && <Industry data={data} />}
        {tab === "cortex" && <Cortex data={data} />}
        {tab === "chat" && <Chat data={data} />}
        {tab === "kpi-reference" && <KpiReference />}
        {tab === "architecture" && <Architecture />}
        {tab === "faq" && <Faq />}
        {tab === "about" && <About />}
        </main>
        <Footer />
      </div>
      <ToastHost />
    </div>
  );
}
